import {
  Box,
  CircleLoader,
  Drawer,
  Flex,
  Header,
  Icon,
  IconButton,
  InputText,
  Text,
} from '@driftt/dds'
import { faArrowLeft } from '@driftt/dds.icons/light/faArrowLeft'
import { faMagnifyingGlass } from '@driftt/dds.icons/light/faMagnifyingGlass'
import React, { useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { RootStateOrAny, useSelector } from 'react-redux'

import { getGuardrailHistoryData } from 'api/scenarios'

import { HistoryCard } from './components/HistoryCard/HistoryCard'

interface GuardrailHistoryItem {
  historyId: string
  createdAt: string
  replyGuideline: string
  createdByName: string
}

interface GuardrailHistoryDrawerProps {
  scenarioId: string
  open: boolean
  onClose: () => void
  handleBack: () => void
  onSelect: (replyGuideline: string) => void
}

const GuardrailHistoryDrawer = ({
  scenarioId,
  open,
  onClose,
  handleBack,
  onSelect,
}: GuardrailHistoryDrawerProps) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCardId, setSelectedCardId] = useState('')

  const orgId: string = useSelector(({ user }: RootStateOrAny) => user.orgId)
  const { data: guardrailHistoryData = [], isLoading } = useQuery({
    queryKey: ['guardrailHistory', orgId, scenarioId],
    queryFn: async () => {
      const response = await getGuardrailHistoryData({ orgId, scenarioId })
      return response.data
    },
    enabled: !!(orgId && scenarioId),
  })

  const filteredHistoryData = useMemo(() => {
    if (!searchTerm.trim()) {
      return guardrailHistoryData
    }

    return guardrailHistoryData.filter((item: GuardrailHistoryItem) => {
      const searchLower: string = searchTerm.toLowerCase()
      return item.replyGuideline?.toLowerCase().includes(searchLower)
    })
  }, [guardrailHistoryData, searchTerm])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  const renderWrapper = (children: React.ReactNode) => (
    <Flex
      direction="column"
      alignItems="center"
      justifyContent="center"
      css={{ textAlign: 'center', padding: '$6' }}
    >
      {children}
    </Flex>
  )

  const handleClick = (id: string, replyGuideline: string) => {
    if (selectedCardId !== id) {
      setSelectedCardId(id)
    } else {
      if (selectedCardId === id && onSelect) {
        onSelect(replyGuideline)
      }
      setSelectedCardId('')
    }
  }

  return (
    <Drawer.Root open={open} onClose={onClose}>
      <Drawer.Dialog>
        <Drawer.Title
          title=""
          prefix={
            <Flex direction="row" alignItems="center" fullWidth>
              <IconButton icon={faArrowLeft} onClick={handleBack} title="" />
              <Header variant="h3">Reply guidelines history</Header>
            </Flex>
          }
        />
        <Drawer.Content
          css={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        >
          <Box css={{ marginBottom: '$3', flexShrink: 0 }}>
            <InputText
              label="Search"
              hideLabel
              placeholder="Search"
              size="medium"
              startAdornment={<Icon color="blue3" icon={faMagnifyingGlass} />}
              onChange={handleSearchChange}
              value={searchTerm}
              disabled={isLoading}
              aria-label="Search reply guidelines history"
            />
          </Box>
          {isLoading ? (
            renderWrapper(<CircleLoader size="extra-large" variant="dark" />)
          ) : filteredHistoryData.length === 0 ? (
            renderWrapper(
              <Text variant="bodyBold" css={{ color: '$gray11' }}>
                No Data Available
              </Text>
            )
          ) : (
            <Box
              css={{
                flex: 1,
                overflowY: 'auto',
                minHeight: 0,
              }}
            >
              {filteredHistoryData.map((item: GuardrailHistoryItem) => (
                <HistoryCard
                  historyId={item.historyId}
                  datetime={item.createdAt}
                  replyGuideline={item.replyGuideline}
                  createdByName={item.createdByName}
                  handleClick={() =>
                    handleClick(item.historyId, item.replyGuideline)
                  }
                  selectedCardId={selectedCardId}
                />
              ))}
            </Box>
          )}
        </Drawer.Content>
      </Drawer.Dialog>
    </Drawer.Root>
  )
}
export default GuardrailHistoryDrawer
